import { MetricField as Field, MetricUnit as Unit } from '@/__generated__/graphql';
import { type I18n } from '@lingui/core';
import { getUnit } from './units';

export const getUnitsPerMetric = (metric: Field, i18n: I18n) => {
  const get = (unit: Unit) => {
    const unitAttributes = getUnit(unit, i18n);
    if (!unitAttributes) throw new Error(`Unit ${unit} not specified on getUnit util`);

    return {
      value: unitAttributes.value,
      enumValue: unit,
      label: unitAttributes.short,
    };
  };

  if ([Field.Flow, Field.FlowRaw, Field.DgaFlow].includes(metric)) {
    return [Unit.LiterPerSecond, Unit.CubicMeterPerSecond, Unit.CubicMeterPerHour].map(get);
  }

  if (
    [
      Field.Level,
      Field.OriginalDistance,
      Field.OriginalDistanceRaw,
      Field.Position,
      Field.LevelRaw,
      Field.SnowLevel,
      Field.SnowLevelRaw,
      Field.SnowLevelNonCalibrated,
      Field.SnowWaterEquivalent,
      Field.SnowWaterEquivalentRaw,
      Field.SnowWaterEquivalentNonCalibrated,
    ].includes(metric)
  ) {
    return [Unit.Millimeter, Unit.Centimeter, Unit.Meter].map(get);
  }

  if (metric === Field.Angle) {
    return [Unit.Degree].map(get);
  }

  if (metric === Field.Velocity) {
    return [Unit.CentimeterPerSecond, Unit.MillimeterPerSecond].map(get);
  }

  if (
    [
      Field.VolumeM3,
      Field.VolumeDay,
      Field.VolumeHour,
      Field.VolumeMonth,
      Field.StoredVolume,
      Field.CalculatedVolume,
      Field.AccumulatedVol,
      Field.AccumulatedVolRaw,
      Field.OriginalVol,
      Field.OriginalVolRaw,
      Field.AccumulatedVolume,
      Field.DgaAccumulatedVolume,
    ].includes(metric)
  ) {
    return [Unit.Liter, Unit.CubicMeter, Unit.CubicHectometer].map(get);
  }

  if (
    [
      Field.Temperature,
      Field.TemperatureRaw,
      Field.SnowTemperature,
      Field.SnowTemperatureRaw,
    ].includes(metric)
  ) {
    return [Unit.Celsius, Unit.Fahrenheit].map(get);
  }

  if (metric === Field.BatterySoc || metric === Field.BatterySocRaw) {
    return [Unit.Percent].map(get);
  }

  if (metric === Field.Conductivity || metric === Field.ConductivityRaw) {
    return [Unit.MicrosiemensPerCentimeter, Unit.MillisiemensPerCentimeter].map(get);
  }

  if (metric === Field.Ph || metric === Field.PhRaw) {
    return [Unit.Ph].map(get);
  }

  if (metric === Field.TurbidityNtu || metric === Field.TurbidityNtuRaw) {
    return [Unit.Ntu].map(get);
  }

  if (metric === Field.TurbidityFnu || metric === Field.TurbidityFnuRaw) {
    return [Unit.Fnu].map(get);
  }

  if (
    metric === Field.SolarV ||
    metric === Field.SolarVRaw ||
    metric === Field.BatteryV ||
    metric === Field.BatteryVRaw ||
    metric === Field.Orp ||
    metric === Field.OrpRaw
  ) {
    return [Unit.Volt, Unit.Millivolt].map(get);
  }

  if (
    metric === Field.SolarI ||
    metric === Field.SolarIRaw ||
    metric === Field.ChargeI ||
    metric === Field.ChargeIRaw
  ) {
    return [Unit.Ampere].map(get);
  }

  if (
    metric === Field.SolarP ||
    metric === Field.SolarPRaw ||
    metric === Field.ChargeP ||
    metric === Field.ChargePRaw
  ) {
    return [Unit.Watt].map(get);
  }

  if ([Field.SnowRadiation, Field.SnowRadiationRaw].includes(metric)) {
    return [Unit.WattPerSquareMeter].map(get);
  }

  if ([Field.SnowWeight, Field.SnowWeightRaw].includes(metric)) {
    return [Unit.Kilogram].map(get);
  }

  if ([Field.DissolvedOxygen, Field.DissolvedOxygenRaw].includes(metric)) {
    return [Unit.MilligramPerLiter].map(get);
  }

  if ([Field.DissolvedOxygenSaturation, Field.DissolvedOxygenSaturationRaw].includes(metric)) {
    return [Unit.Percent].map(get);
  }

  if ([Field.Allocation].includes(metric)) {
    return [Unit.LiterPerSecondPerAction, Unit.CubicMeterPerSecondPerAction].map(get);
  }

  return [];
};
